/**
 * Espace de travail de l'enseignant : ses cours, tous statuts confondus.
 *
 * La création se fait ici plutôt que sur un écran dédié : titre et domaine
 * suffisent (docs/11 §6), et un formulaire en trois étapes avant d'avoir la
 * moindre page ferait abandonner à la première.
 */
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useCourseDomains, useCreateCourse, useMyCourses } from "@/features/catalog/api/useCatalog";
import { CourseCard } from "@/features/catalog/components/CourseCard";
import { useApiErrorMessage } from "@/features/auth/lib/useApiErrorMessage";
import { Alert } from "@/shared/ui/Alert";
import { Button } from "@/shared/ui/Button";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { LoadMore } from "@/shared/ui/LoadMore";
import { Skeleton } from "@/shared/ui/Skeleton";

const FIELD_CLASSES =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm " +
  "dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100";

export function MyCoursesPage() {
  const { t } = useTranslation("catalog");
  // Les libellés du suivi vivent dans `learning` : ce sont ses écrans, et les
  // recopier dans `catalog` ferait deux traductions du même mot, dont l'une
  // finirait par ne plus suivre l'autre.
  const { t: teaching } = useTranslation("learning");
  const navigate = useNavigate();
  const domains = useCourseDomains();
  const courses = useMyCourses({});
  const createCourse = useCreateCourse();
  const errorMessage = useApiErrorMessage();

  const [title, setTitle] = useState("");
  const [domainId, setDomainId] = useState("");

  const rows = courses.data?.pages.flatMap((page) => page.data);

  const submit = () => {
    createCourse.mutate(
      { title, domain_id: domainId },
      {
        onSuccess: (course) => {
          setTitle("");
          // On entre directement dans l'éditeur : un cours créé mais laissé
          // dans une liste est un cours qu'on oublie de remplir.
          void navigate({ to: "/teach/courses/$courseId", params: { courseId: course.course.id } });
        },
      },
    );
  };

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 px-4 py-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">{t("mine.title")}</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">{t("mine.subtitle")}</p>
        {/* Les deux écrans de suivi (docs/12 §6 et §4) s'atteignent depuis
            l'espace de travail, et non depuis la barre de session : ils
            n'auraient rien à dire à un élève, et le serveur lui répondrait 403.
            Ici, on sait déjà pourquoi on est là. */}
        <nav className="flex flex-wrap gap-4 pt-2 text-sm">
          <Link to="/teach/dashboard" className="text-brand-600 hover:underline">
            {teaching("teaching.title")}
          </Link>
          <Link to="/teach/grading" className="text-brand-600 hover:underline">
            {teaching("grading.title")}
          </Link>
        </nav>
      </header>

      <Card title={t("mine.createTitle")}>
        <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="new-course-title">
              {t("mine.courseTitle")}
            </label>
            <input
              id="new-course-title"
              className={FIELD_CLASSES}
              value={title}
              onChange={(event) => {
                setTitle(event.target.value);
              }}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="new-course-domain">
              {t("catalog.domain")}
            </label>
            <select
              id="new-course-domain"
              className={FIELD_CLASSES}
              value={domainId}
              onChange={(event) => {
                setDomainId(event.target.value);
              }}
            >
              <option value="">{t("mine.chooseDomain")}</option>
              {domains.data?.map((domain) => (
                <option key={domain.id} value={domain.id}>
                  {domain.name_i18n["fr"] ?? domain.slug}
                </option>
              ))}
            </select>
          </div>
          <Button
            disabled={title.trim() === "" || domainId === "" || createCourse.isPending}
            onClick={submit}
          >
            {t("mine.create")}
          </Button>
        </div>
        {createCourse.isError && (
          <Alert tone="error" className="mt-3">
            {errorMessage(createCourse.error)}
          </Alert>
        )}
      </Card>

      {courses.isPending && <Skeleton className="h-40 w-full" label={t("mine.title")} />}
      {courses.isError && <ErrorState error={courses.error} />}

      {rows !== undefined && rows.length === 0 && (
        <Card>
          <p className="text-sm text-slate-500 dark:text-slate-400">{t("mine.empty")}</p>
        </Card>
      )}

      {rows !== undefined && rows.length > 0 && (
        <>
          <ul className="grid gap-4 sm:grid-cols-2">
            {rows.map((course) => (
              <li key={course.id}>
                <Link
                  to="/teach/courses/$courseId"
                  params={{ courseId: course.id }}
                  className="block"
                >
                  <CourseCard course={course} showStatus />
                </Link>
              </li>
            ))}
          </ul>
          <LoadMore
            shown={rows.length}
            hasMore={courses.hasNextPage}
            isLoading={courses.isFetchingNextPage}
            onLoadMore={() => {
              void courses.fetchNextPage();
            }}
          />
        </>
      )}
    </div>
  );
}
